<template>
    <div id="customer_contract_edit" v-if="contract.identity">
        <div class="ui segment">
            <div class="ui horizontal divider">
                <h3>
                    <i class="clipboard icon"></i>
                    {{ $t('ui.pages.customers.contracts') }}
                </h3>
            </div>
            <div class="ui form">
                <div class="fields">
                    <div class="eight wide required field" data-column="name">
                        <label>{{ $t('models.attributes.customer_contracts.name') }}</label>
                        <input
                            type="text"
                            v-model="contract.name">
                    </div>
                    <div class="eight wide required field column" data-column="number">
                        <label>{{ $t('models.attributes.customer_contracts.number') }}</label>
                        <input
                            type="text"
                            v-model="contract.number">
                    </div>
                </div>
                <div class="fields">
                    <div class="eight wide required field" data-column="carrier_class">
                        <label>{{ $t('models.attributes.customer_contracts.carrier_class') }}</label>
                        <select v-if="$store.state.provider.node" class="ui dropdown" v-dropdown v-model="contract.carrier_class">
                            <option value="">{{ $t('ui.general.please_select') }}</option>
                            <option :value="$store.state.provider.node.carrier_class">{{ $store.state.provider.node.company_short_name }}</option>
                            <option value="Carrier::LMSProxy">自動轉運</option>
                        </select>
                        <div v-else class="ui inline active loader"></div>
                    </div>
                    <div class="eight wide field column">
                        <label>{{ $t('models.attributes.customer_contracts.proxy_contract') }}</label>
                        <select v-if="current_store.provider_contracts" class="ui dropdown" v-dropdown v-model="contract.proxy_contract_identity">
                            <option value="">{{ $t('ui.general.please_select') }}</option>
                            <option v-for="provider_contract in current_store.provider_contracts" :value="provider_contract.identity">{{ provider_contract.name }}</option>
                        </select>
                        <div v-else class="ui inline active loader"></div>
                    </div>
                </div>
                <div class="ui divider"></div>
                <div class="fields">
                    <div class="eight wide field">
                        <div class="ui checkbox" v-checkbox>
                            <input type="checkbox" tabindex="0" class="hidden" v-model="contract.is_automated_fetch_status">
                            <label>
                                是否自動同步貨況
                            </label>
                        </div>
                    </div>
                    <div class="eight wide field">
                        <div class="ui checkbox" v-checkbox>
                            <input type="checkbox" tabindex="0" class="hidden" v-model="contract.is_automated_send_data">
                            <label>
                                是否自動傳送EDI
                            </label>
                        </div>
                    </div>
                </div>
                <div class="fields">
                    <div class="eight wide field" :class="{'required': contract.is_automated_fetch_status}">
                        <label>自動同步貨況設定</label>
                        <input
                            type="text"
                            :disabled="!contract.is_automated_fetch_status"
                            v-model="contract.fetch_status_cron">
                        <p v-if="contract.fetch_status_cron" v-for="cron in human_readable_cron(contract.fetch_status_cron)"><i>會於 {{ cron }} 執行</i></p>
                    </div>
                    <div class="eight wide field column" :class="{'required': contract.is_automated_send_data}">
                        <label>自動傳送EDI設定</label>
                        <input
                            type="text"
                            :disabled="!contract.is_automated_send_data"
                            v-model="contract.send_data_cron">
                        <p v-if="contract.send_data_cron" v-for="cron in human_readable_cron(contract.send_data_cron)"><i>會於 {{ cron }} 執行</i></p>
                    </div>
                </div>
                <div class="ui divider"></div>
                <div class="right floated">
                    <a href="#" v-if="!is_loading" @click.prevent="update_contract()" class="ui blue button">{{ $t('ui.general.update') }}</a>
                    <a href="#" v-else class="ui blue button">
                        {{ $t('ui.general.updating') }}...
                    </a>
                </div>
                <div class="clear"></div>
            </div>
        </div>
        <div class="ui segment">
            <div class="ui horizontal divider">
                <h3>
                    <i class="code icon"></i>
                    {{ $t('ui.pages.customer_contracts.additional_information') }}
                </h3>
            </div>
            <div class="ui form">
                <div class="fields">
                    <div class="sixteen wide red field">
                        <label>{{ $t('models.attributes.customer_contracts.note') }}</label>
                        <textarea v-model="contract.note" rows="4"></textarea>
                    </div>
                    <div class="sixteen wide field">
                        <label>{{ $t('models.attributes.customer_contracts.third_party_api_data') }}</label>
                        <codemirror
                            v-model="contract.third_party_api_data"
                            :options="code_mirror_options">
                        </codemirror>
                    </div>
                </div>
                <div class="ui divider"></div>
                <div class="right floated">
                    <a href="#" v-if="!is_loading" @click.prevent="update_contract()" class="ui blue button">{{ $t('ui.general.update') }}</a>
                    <a href="#" v-else class="ui blue button">
                        {{ $t('ui.general.updating') }}...
                    </a>
                </div>
                <div class="clear"></div>
            </div>
        </div>
        <div class="ui segment">
            <div class="ui horizontal divider">
                <h3>
                    <i class="dollar sign icon"></i>
                    {{ $t('ui.page_titles.quote_policies') }}
                </h3>
            </div>
            <table class="ui celled table">
                <thead class="center aligned">
                    <tr>
                        <th>名稱</th>
                        <th>開始於</th>
                        <th>結束於</th>
                        <th>條件名稱</th>
                        <th>條件極性</th>
                        <th>條件物料</th>
                        <th>是否啟用</th>
                        <th>備註</th>
                        <th>動作</th>
                    </tr>
                </thead>
                <tbody class="center aligned">
                    <tr v-for="quote_policy in contract.quote_policies">
                        <td>{{ quote_policy.name }}</td>
                        <td>{{ human_datetime(quote_policy.begin_at) }}</td>
                        <td>{{ human_datetime(quote_policy.end_at) }}</td>
                        <td>{{ quote_policy.required_condition }}</td>
                        <td>{{ quote_policy.required_condition_polarity ? '正':'負' }}</td>
                        <td>{{ quote_policy.required_material_class_name }}</td>
                        <td>{{ quote_policy.is_activated ? '啟用':'不啟用' }}</td>
                        <td>{{ quote_policy.note }}</td>
                        <td>
                            <router-link :to="{
                                name: 'provider_quote_policy',
                                params: {
                                    customer_identity: $route.params.customer_identity,
                                    contract_identity: $route.params.contract_identity,
                                    quote_policy_identity: quote_policy.identity
                                }
                            }" class="ui primary button">
                                檢視
                            </router-link>
                            <a class="ui orange button" @click="fetch_csv(quote_policy.identity)">帳單</a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import moment from "moment"
import CronParser from 'cron-parser'
import Validator from '@toolbox/validator/main.js'
import { codemirror } from 'vue-codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/base16-dark.css'
import 'codemirror/mode/javascript/javascript.js'

export default {
    name: "ProviderCustomersContractsEdit",
    mixins: [
        Validator
    ],
    props: {
    },
    data() {
        return {
            is_loading: false,
            contract: {},
            code_mirror_options: {
                tabSize: 4,
                smartIndent: true,
                mode: "application/json",
                theme: "base16-dark",
                lineNumbers: true,
                line: true,
            },
            cron_parser_options: {
                currentDate: moment(),
                tz: 'Asia/Taipei'
            }
        }
    },
    watch: {
    },
    mounted() {
        this.fetch_contract()
    },
    methods: {
        human_readable_cron(cron_string) {
            try {
                var interval = CronParser.parseExpression(cron_string, this.cron_parser_options)
                var crons = []
                crons.push(moment(interval.next().toString()).locale("zh-tw").format("lll"))
                crons.push(moment(interval.next().toString()).locale("zh-tw").format("lll"))
                crons.push(moment(interval.next().toString()).locale("zh-tw").format("lll"))
                return crons
            } catch (e) {
                console.log(e)
            }
        },
        fetch_contract() {
            var url = new RestUrl(this.$store.state.provider.routes.api_v1_provider_contract).parse({
                "contract_identity": this.$route.params.contract_identity,
            })

            return axios.get(url, { headers: { Authorization: localStorage.getItem("authentication_token") }})
                .then(response => {
                    this.contract = response.data.contract
                    if (this.contract.third_party_api_data) {
                        this.contract.third_party_api_data = JSON.stringify(this.contract.third_party_api_data, undefined, 4)
                    }
                })
                .catch(error => {
                    return Promise.reject(error)
                })
        },
        update_contract() {
            if (this.valid()) {
                this.is_loading = true

                // this.contract.third_party_api_data = JSON.parse(this.third_party_api_data)
                var params = this.contract
                var url = new RestUrl(this.$store.state.provider.routes.api_v1_provider_contract_update).parse(
                    {
                        contract_identity: this.contract.identity
                    }
                )

                return axios.post(url, params, { headers: { Authorization: localStorage.getItem("authentication_token") }}).then(response => {
                    if (response.data.status == "success") {
                        // this.$noty.success("更新客戶合約成功")
                        this.$noty.success(
                            this.$t('ui.notifications.success.update__name', {
                                name: this.$t('models.contracts')
                            })
                        )
                        this.is_loading = false
                        this.$router.push({name: 'provider_customers'})
                    } else {
                        // this.$noty.error("更新客戶合約失敗")
                        this.$noty.error(
                            this.$t('ui.notifications.error.create__name', {
                                name: this.$t('models.contracts')
                            })
                        )
                        this.is_loading = false
                    }
                })
                .catch(error => {
                    // this.$noty.error("更新客戶合約失敗")
                    this.$noty.error(
                        this.$t('ui.notifications.error.update__name', {
                            name: this.$t('models.contracts'),
                        })
                    )
                    this.is_loading = false
                })
            }
        }
    },
    validator: {
        target: "contract",
        localize_key: "customer_contracts",
        input_alert: true,
        attributes: [
            {
                name: "name",
                required: true
            },
            {
                name: "number",
                required: true
            },
            {
                name: "carrier_class",
                required: true
            }
        ]
    },
    computed: {
        current_store() {
            return this.$store.state.provider.customers_module
        },
        current_customer() {
            return _.findLast(this.current_store.customers, customer => {
                return customer.identity == this.contract.customer_identity
            })
        },

    },
    components: {
        codemirror
    }
}
</script>

<style lang="scss">
</style>
